import React, { useState, useEffect } from "react";
import axios from "axios";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { Card } from "@/components/ui/card";
import { Loader2, Leaf, HeartPulse, Sprout, Brain, ChevronDown, ChevronUp } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const PATTERN_META = {
  mediterranean: {
    icon: Leaf,
    label_bg: "Средиземноморска",
    label_en: "Mediterranean",
    hint_bg: "PREDIMED — 30% по-малък риск от ССЗ",
    hint_en: "PREDIMED — 30% lower CVD risk",
    reference: "Sofi 2014 · Trichopoulou 2003",
  },
  dash: {
    icon: HeartPulse,
    label_bg: "DASH",
    label_en: "DASH",
    hint_bg: "Понижава кръвно налягане (NEJM 2001)",
    hint_en: "Lowers blood pressure (NEJM 2001)",
    reference: "Fung 2008",
  },
  hpdi: {
    icon: Sprout,
    label_bg: "Plant-Based (hPDI)",
    label_en: "Plant-Based (hPDI)",
    hint_bg: "20% ↓ T2D риск (Satija 2017)",
    hint_en: "20% ↓ T2D risk (Satija 2017)",
    reference: "Satija 2017",
  },
  mind: {
    icon: Brain,
    label_bg: "MIND (когнитивно)",
    label_en: "MIND (cognitive)",
    hint_bg: "53% ↓ Алцхаймер риск (Morris 2015)",
    hint_en: "53% ↓ Alzheimer risk (Morris 2015)",
    reference: "Morris 2015",
  },
};


function PatternBar({ pct, color, isDark }) {
  return (
    <div className={`h-1.5 rounded-full overflow-hidden ${isDark ? "bg-[hsl(223,47%,12%)]" : "bg-gray-100"}`}>
      <div className="h-full rounded-full transition-all duration-500"
        style={{ width: `${Math.max(3, pct)}%`, backgroundColor: color }}
      />
    </div>
  );
}


export default function DietaryPatternsCard() {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/nutrition/dietary-patterns`, { params: { days } })
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });

    const refresh = () => {
      axios.get(`${API}/nutrition/dietary-patterns`, { params: { days } })
        .then((res) => setData(res.data))
        .catch(() => {});
    };
    window.addEventListener("meal-added", refresh);
    window.addEventListener("meal-log-changed", refresh);
    return () => {
      cancelled = true;
      window.removeEventListener("meal-added", refresh);
      window.removeEventListener("meal-log-changed", refresh);
    };
  }, [days]);

  if (loading) {
    return (
      <Card className={`p-4 ${isDark ? "bg-[hsl(223,47%,10%)] border-[hsl(223,47%,18%)]" : "bg-white"}`} data-testid="dietary-patterns-loading">
        <Loader2 className="w-5 h-5 animate-spin text-[#0EA5E9]" />
      </Card>
    );
  }

  if (!data || data.meals_count === 0) {
    return (
      <Card className={`p-5 ${isDark ? "bg-[hsl(223,47%,10%)] border-[hsl(223,47%,18%)]" : "bg-white"}`} data-testid="dietary-patterns-empty">
        <div className="flex items-start gap-3">
          <Leaf className="w-5 h-5 text-[#10B981] mt-0.5 flex-shrink-0" />
          <div>
            <h3 className={`text-sm font-semibold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
              {bg ? "Хранителни модели" : "Dietary Patterns"}
            </h3>
            <p className={`text-xs mt-1 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
              {bg ? "Запишете поне едно хранене, за да видите оценка по 4 валидирани модела (Средиземноморска, DASH, Plant-based, MIND)." : "Log at least one meal to see 4 validated pattern scores."}
            </p>
          </div>
        </div>
      </Card>
    );
  }

  const patterns = ["mediterranean", "dash", "hpdi", "mind"];

  return (
    <Card className={`p-5 ${isDark ? "bg-[hsl(223,47%,10%)] border-[hsl(223,47%,18%)]" : "bg-white border-gray-100"}`} data-testid="dietary-patterns-card">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className={`text-sm font-semibold flex items-center gap-1.5 ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
            <Leaf className="w-4 h-4 text-[#10B981]" />
            {bg ? "Хранителни модели" : "Dietary Patterns"}
          </h3>
          <p className={`text-[10px] mt-0.5 ${isDark ? "text-gray-500" : "text-gray-400"}`}>
            {bg ? "4 клинично валидирани модела" : "4 clinically validated patterns"}
          </p>
        </div>
        <div className="flex gap-1" data-testid="dietary-days-tabs">
          {[7, 14, 30].map((d) => (
            <button key={d}
              onClick={() => setDays(d)}
              className={`px-2 py-0.5 rounded text-[10px] font-semibold transition-all ${
                days === d
                  ? "bg-gradient-to-r from-[#06B6D4] to-[#6366F1] text-white"
                  : isDark ? "bg-[hsl(223,47%,14%)] text-gray-400" : "bg-gray-100 text-gray-500"
              }`}
              data-testid={`dietary-days-${d}`}>
              {bg ? `${d}д` : `${d}d`}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        {patterns.map((key) => {
          const p = data[key];
          if (!p) return null;
          const meta = PATTERN_META[key];
          const Icon = meta.icon;
          const isExp = expanded === key;
          return (
            <div key={key} data-testid={`pattern-${key}`}>
              <button
                className={`w-full flex items-center justify-between gap-2 p-0 bg-transparent ${isExp ? "mb-2" : "mb-1"}`}
                onClick={() => setExpanded(isExp ? null : key)}
              >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <Icon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: p.color }} />
                  <span className={`text-xs font-medium truncate ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    {bg ? meta.label_bg : meta.label_en}
                  </span>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <span className={`text-xs font-bold`} style={{ color: p.color }}>{p.percent}%</span>
                  <span className={`text-[10px] ${isDark ? "text-gray-500" : "text-gray-400"}`}>{bg ? p.label_bg : (p.label || p.label_bg)}</span>
                  {isExp ? <ChevronUp className="w-3 h-3 text-gray-400" /> : <ChevronDown className="w-3 h-3 text-gray-400" />}
                </div>
              </button>
              <PatternBar pct={p.percent} color={p.color} isDark={isDark} />
              {isExp && (
                <div className={`mt-2 space-y-1 ${isDark ? "bg-[hsl(223,47%,7%)]" : "bg-gray-50"} rounded-lg p-2`} data-testid={`pattern-${key}-details`}>
                  <p className={`text-[10px] italic mb-1.5 ${isDark ? "text-gray-500" : "text-gray-500"}`}>
                    {bg ? meta.hint_bg : meta.hint_en} · {meta.reference}
                  </p>
                  {(p.components || []).slice(0, 10).map((c, i) => (
                    <div key={c.name ? `${c.name}-${i}` : `c-${i}`} className="flex items-center justify-between text-[10px]">
                      <span className={`${c.optional ? "italic opacity-60" : ""} ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                        {c.name}
                      </span>
                      <span className={c.achieved || (c.points && c.points > 0) ? "text-[#10B981] font-semibold" : (isDark ? "text-gray-500" : "text-gray-400")}>
                        {c.achieved !== undefined ? (c.achieved ? "✓" : "—") : (c.points !== undefined ? `${c.points} pts` : "—")}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <p className={`text-[10px] italic mt-3 pt-3 border-t ${isDark ? "text-gray-500 border-[hsl(223,47%,15%)]" : "text-gray-400 border-gray-100"}`}>
        {bg
          ? `Базирано на ${data.meals_count} записани храни за последните ${data.days_analyzed} дни`
          : `Based on ${data.meals_count} meals over the last ${data.days_analyzed} days`}
      </p>
    </Card>
  );
}
